import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { numberShortener } from '../../../../../utils/numberFormatter';
import { constantStringToHumanized } from '../../../../../utils/stringModifier';
import { HtmlTooltip } from '../../../../Tooltip';
import {
  Admin,
  AudienceSet,
  AudienceSetTargetsData,
  Brand,
} from '../../../../../types';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import {
  getCustomAudiences,
  shareCustomAudiences,
} from '../../../../../services/diy';
import { PrimaryButton } from '../../../../Buttons';
import PopupModal from '../../../../Modal';
import ModalHeader from '../../../../Modal/ModalHeader';
import LookalikeAudienceTable from '../../../../Tables/AudienceList/LookalikeAudienceTable';
import { CustomAudience } from '../../../../../types/IAudience';
import { fetchAdminData } from '../../../../../services/admin';
import { DiySettings } from '../../../../../types/IDiy';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import Spinner from '../../../../Spinner';

interface CustomAudienceSetFormProps {
  formValues: AudienceSet;
  setFormValues: Dispatch<SetStateAction<AudienceSet>>;
  brand: Brand;
  diySettings: DiySettings;
}

const CustomAudienceSetForm: FC<CustomAudienceSetFormProps> = ({
  formValues,
  setFormValues,
  brand,
  diySettings,
}) => {
  const dispatch = useDispatch();
  const [customAudiences, setCustomAudiences] = useState<
    AudienceSetTargetsData[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [excludeCustomAudience, setExcludeCustomAudience] =
    useState<boolean>(false);
  const [openRequestForm, setOpenRequestForm] = useState<boolean>(false);
  const [shareableCustomAudiences, setShareableCustomAudiences] = useState<
    CustomAudience[]
  >([]);
  const [adminData, setAdminData] = useState<Admin>(null);
  const [selectedCustomAudiences, setSelectedCustomAudiences] = useState<
    CustomAudience[]
  >([]);
  const [shareLoading, setShareLoading] = useState<boolean>(false);

  useEffect(() => {
    getAdminData();
  }, []);

  useEffect(() => {
    if (brand) {
      getFbCustomAudiences();
    }
  }, [brand]);

  useEffect(() => {
    if (
      diySettings?.fbAdAccount?.accountId &&
      adminData?.shareableLookalikeAudienceIds?.length > 0
    ) {
      getShareableLookalikeAudiences();
    }
  }, [
    diySettings?.fbAdAccount?.accountId,
    adminData?.shareableLookalikeAudienceIds,
  ]);

  const getFbCustomAudiences = async () => {
    try {
      setLoading(true);

      const response = await getCustomAudiences(brand?._id);

      if (response.data?.length === 1) {
        setFormValues({
          ...formValues,
          targets: {
            ...formValues?.targets,
            custom_audiences: [response.data[0]],
          },
        });
      }

      setCustomAudiences(response.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getShareableLookalikeAudiences = async () => {
    try {
      setFetchLoading(true);

      const response = await getCustomAudiences();

      const lookalikeAudiences: CustomAudience[] =
        response?.data?.filter(
          (audience: CustomAudience) => audience.subtype === 'LOOKALIKE',
        ) || [];

      const filteredShareableAudiences = lookalikeAudiences.filter(
        (audience: CustomAudience) => {
          return adminData?.shareableLookalikeAudienceIds?.includes(
            audience.id,
          );
        },
      );

      setShareableCustomAudiences(filteredShareableAudiences);
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const getAdminData = async () => {
    try {
      const response = await fetchAdminData();

      setAdminData(response.data);
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleOnChangeExclude = (e: any) => {
    if (!e.target.checked) {
      setFormValues({
        ...formValues,
        targets: {
          ...formValues?.targets,
          excluded_custom_audiences: [],
        },
      });
    }

    setExcludeCustomAudience(e.target.checked);
  };

  const handleOpenRequestForm = () => {
    setOpenRequestForm((prev) => !prev);
  };

  const shareLookalikeAudiences = async () => {
    try {
      setShareLoading(true);

      await shareCustomAudiences(brand?._id, selectedCustomAudiences);

      const sharedAudienceCount = selectedCustomAudiences.length;

      let temp: AudienceSetTargetsData[] = [...customAudiences];
      selectedCustomAudiences.forEach((customAudience: CustomAudience) => {
        const params: AudienceSetTargetsData = {
          id: customAudience.id,
          name: customAudience.name,
          subtype: customAudience.subtype,
          approximate_count_lower_bound:
            customAudience.approximate_count_lower_bound,
          approximate_count_upper_bound:
            customAudience.approximate_count_upper_bound,
          description: customAudience.description,
        };

        temp = [...temp, params];
      });

      setCustomAudiences(temp);
      setSelectedCustomAudiences([]);
      handleOpenRequestForm();
      dispatch(
        toggleAlert({
          toggle: true,
          message: `${sharedAudienceCount} new Lookalike Audience has been shared to your ad account`,
        }),
      );
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);
      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setShareLoading(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PrimaryButton
            title="Request Lookalike Audience"
            type="button"
            handleOnClick={handleOpenRequestForm}
            marginRight5
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            id="customAudiences"
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  <Box
                    component="div"
                    sx={{
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Typography variant="body1" component="div">{`${
                      option.name
                    } (${constantStringToHumanized(
                      option.subtype,
                    )})`}</Typography>

                    <Typography variant="caption" component="div">
                      {option.id}
                    </Typography>

                    <Typography
                      variant="caption"
                      component="div"
                    >{`Estimated Audience Size: ${numberShortener(
                      option.approximate_count_lower_bound,
                    )} - ${numberShortener(
                      option.approximate_count_lower_bound,
                    )}`}</Typography>
                  </Box>
                </li>
              );
            }}
            options={
              customAudiences?.sort((a, b) => -b.name?.localeCompare(a.name)) ||
              []
            }
            value={formValues?.targets?.custom_audiences || []}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Custom Audience"
                fullWidth
                size="small"
                placeholder="Select a custom audiences"
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? <Spinner /> : null}

                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            )}
            disableClearable
            onChange={async (e: any, values: any[]) => {
              setFormValues({
                ...formValues,
                targets: {
                  ...formValues?.targets,
                  custom_audiences: values,
                },
              });
            }}
            loading={loading}
            multiple
            getOptionDisabled={(option) => {
              const ids = [
                ...(formValues?.targets?.excluded_custom_audiences || []),
                ...(formValues?.targets?.custom_audiences || []),
              ]?.map((item) => item.id);

              return ids?.includes(option.id);
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={
                  formValues?.targets?.targeting_relaxation_types?.lookalike ===
                  1
                }
              />
            }
            label={
              <Fragment>
                <Box
                  sx={{
                    flexDirection: 'row',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: '5px' }}>
                    Enable Advantage Lookalike
                  </Typography>

                  <HtmlTooltip
                    disableInteractive
                    title={
                      <Fragment>
                        <Box component="div" sx={{ fontWeight: 'bold' }}>
                          Advantage lookalike
                        </Box>

                        <Box component="div" sx={{ marginBottom: '10px' }}>
                          This expands your audience beyond your lookalike to
                          reach more people. We recommend this option because
                          your campaign will be using our most advanced ad
                          technology to help maximize performance.{' '}
                          <a
                            href="https://www.facebook.com/business/help/1212225059146059"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Learn more about Advantage lookalike.
                          </a>
                        </Box>

                        <Box component="div" sx={{ marginBottom: '10px' }}>
                          Because your audience is expanded through automation,
                          the estimated audience size will not reflect the total
                          number of Account Center accounts that meet the
                          audience criteria.
                        </Box>
                      </Fragment>
                    }
                  >
                    <InfoTwoToneIcon
                      sx={{
                        fontSize: '18px',
                        color: '#096F4D',
                      }}
                    />
                  </HtmlTooltip>
                </Box>
              </Fragment>
            }
            value={
              formValues?.targets?.targeting_relaxation_types?.lookalike === 1
            }
            onChange={(e: any) =>
              setFormValues({
                ...formValues,
                targets: {
                  ...formValues?.targets,
                  targeting_relaxation_types: {
                    ...formValues?.targets?.targeting_relaxation_types,
                    lookalike: e.target.checked ? 1 : 0,
                  },
                },
              })
            }
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={
                  formValues?.targets?.targeting_automation
                    ?.advantage_audience === 1
                }
              />
            }
            label={
              <Fragment>
                <Box
                  sx={{
                    flexDirection: 'row',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: '5px' }}>
                    Enable Advantage+ Audience
                  </Typography>

                  <HtmlTooltip
                    title={
                      <Fragment>
                        <Box component="div" sx={{ fontWeight: 'bold' }}>
                          Advantage+ Audience
                        </Box>

                        <Box component="div" sx={{ marginBottom: '10px' }}>
                          Advantage+ Audience on Facebook uses AI to optimize
                          audience targeting for your ads, aiming to reach
                          people most likely to respond and achieve your goals.
                          This feature can automatically expand your audience
                          beyond your initial selections if AI determines it
                          will lead to better performance. It's a hybrid
                          approach that allows you to provide some audience
                          suggestions while still leveraging Meta's AI to refine
                          and potentially expand the reach.{' '}
                          <a
                            href="https://www.facebook.com/business/help/273363992030035?id=1629569087788063"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Learn more about Advantage+ Audience.
                          </a>
                        </Box>

                        <Box component="div" sx={{ marginBottom: '10px' }}>
                          Because your audience is expanded through automation,
                          the estimated audience size will not reflect the total
                          number of Account Center accounts that meet the
                          audience criteria.
                        </Box>
                      </Fragment>
                    }
                  >
                    <InfoTwoToneIcon
                      sx={{
                        fontSize: '18px',
                        color: '#096F4D',
                      }}
                    />
                  </HtmlTooltip>
                </Box>
              </Fragment>
            }
            value={
              formValues?.targets?.targeting_automation?.advantage_audience ===
              1
            }
            onChange={(e: any) =>
              setFormValues({
                ...formValues,
                targets: {
                  ...formValues?.targets,
                  targeting_automation: {
                    ...formValues?.targets?.targeting_automation,
                    advantage_audience: e.target.checked ? 1 : 0,
                  },
                },
              })
            }
          />
        </Grid>

        {formValues?.targets?.targeting_automation?.advantage_audience === 1 ? (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', color: 'red' }}
            >
              Advantage+ Audience Enabled: You can select a minimum age between
              18 and 25, and a maximum age of 65 or above.
            </Typography>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={
                  !!formValues?.targets?.excluded_custom_audiences?.length ||
                  excludeCustomAudience
                }
              />
            }
            label="Exclude Custom Audience"
            value={
              !!formValues?.targets?.excluded_custom_audiences?.length ||
              excludeCustomAudience
            }
            onChange={handleOnChangeExclude}
          />
        </Grid>

        {!!formValues?.targets?.excluded_custom_audiences?.length ||
        excludeCustomAudience ? (
          <Grid item xs={12}>
            <Autocomplete
              id="excludedCustomAudiences"
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    <Box
                      component="div"
                      sx={{
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Typography variant="body1" component="div">{`${
                        option.name
                      } (${constantStringToHumanized(
                        option.subtype,
                      )})`}</Typography>

                      <Typography variant="caption" component="div">
                        {option.id}
                      </Typography>

                      <Typography
                        variant="caption"
                        component="div"
                      >{`Estimated Audience Size: ${numberShortener(
                        option.approximate_count_lower_bound,
                      )} - ${numberShortener(
                        option.approximate_count_lower_bound,
                      )}`}</Typography>
                    </Box>
                  </li>
                );
              }}
              options={
                customAudiences?.sort(
                  (a, b) => -b.name?.localeCompare(a.name),
                ) || []
              }
              value={formValues?.targets?.excluded_custom_audiences || []}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Excluded Custom Audience"
                  fullWidth
                  size="small"
                  placeholder="Select a custom audiences"
                  required
                  InputLabelProps={{ shrink: true }}
                />
              )}
              disableClearable
              onChange={async (e: any, values: any[]) => {
                setFormValues({
                  ...formValues,
                  targets: {
                    ...formValues?.targets,
                    excluded_custom_audiences: values,
                  },
                });
              }}
              multiple
              getOptionDisabled={(option) => {
                const ids = [
                  ...(formValues?.targets?.excluded_custom_audiences || []),
                  ...(formValues?.targets?.custom_audiences || []),
                ]?.map((item) => item.id);

                return ids?.includes(option.id);
              }}
            />
          </Grid>
        ) : null}
      </Grid>

      <PopupModal open={openRequestForm} handleClose={handleOpenRequestForm}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ModalHeader title="Shareable Lookalike Audiences" />
          </Grid>

          <Grid item xs={12}>
            <LookalikeAudienceTable
              customAudiences={shareableCustomAudiences}
              loading={fetchLoading}
              share
              accountId={diySettings?.fbAdAccount?.accountId}
              setSelectedAudiences={setSelectedCustomAudiences}
            />
          </Grid>

          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            {selectedCustomAudiences.length > 0 ? (
              <PrimaryButton
                title="Send Request"
                type="button"
                handleOnClick={shareLookalikeAudiences}
                loading={shareLoading}
              />
            ) : null}
          </Grid>
        </Grid>
      </PopupModal>
    </Box>
  );
};

export default CustomAudienceSetForm;
